import { useState, useCallback } from 'react';
import { queryService } from '../../services/queryService';
import { useSnackbar } from '../../components/common/Snackbar';

/**
 * Hook to manage query drafts (server-side persistence via draft-response API).
 * Drafts are stored as thread entries with isDraft=true in the backend.
 * The frontend derives draft list from the query's thread array.
 *
 * @param {string} queryId - Query ID
 * @param {boolean} open - Whether modal is open
 * @returns {Object} Drafts state and handlers
 */
export const useQueryDrafts = (queryId, open) => {
  const { showSnackbar } = useSnackbar();

  const [isSendingDraft, setIsSendingDraft] = useState(false);

  /**
   * Extract draft entries from the query's thread (derived from query object).
   * Called by the parent component; not stored as separate state.
   */
  const getDraftsFromThread = useCallback((thread, currentUserId) => {
    if (!thread || !currentUserId) return [];
    return thread.filter(
      (t) => t.isDraft === true && (t.sentBy?._id || t.sentBy)?.toString() === currentUserId
    );
  }, []);

  /**
   * Save draft handler — creates or updates a draft response on the server.
   * Files are uploaded immediately, so they persist across page refreshes.
   */
  const handleSaveDraft = useCallback((
    responseMessage,
    attachments,
    canRespond,
    setIsSavingDraft,
    setResponseError,
    setResponseMessage,
    setAttachments,
    fileInputRef
  ) => {
    return async (event) => {
      event.preventDefault();

      if (!canRespond) return;

      if (!responseMessage.trim()) {
        setResponseError('Please enter your response message before saving.');
        return;
      }

      setIsSavingDraft(true);
      setResponseError('');

      try {
        const result = await queryService.saveDraftResponse(queryId, {
          message: responseMessage,
          attachments: attachments, // actual File objects — uploaded to server
        });

        // Clear form after saving
        setResponseMessage('');
        setAttachments([]);
        setResponseError('');
        if (fileInputRef?.current) {
          fileInputRef.current.value = '';
        }

        showSnackbar('Draft saved successfully (not visible to raiser)', 'success');

        // Return the updated query so the parent can refresh
        return result.query;
      } catch (error) {
        // console.error('Error saving draft:', error);
        const msg = error.response?.data?.message || error.message || 'Failed to save draft. Please try again.';
        showSnackbar(msg, 'error');
      } finally {
        setIsSavingDraft(false);
      }
    };
  }, [queryId, showSnackbar]);

  /**
   * Delete a server-side draft.
   */
  const deleteDraft = useCallback(async (draftThreadId) => {
    try {
      const updatedQuery = await queryService.deleteDraftResponse(queryId, draftThreadId);
      showSnackbar('Draft deleted', 'success');
      return updatedQuery;
    } catch (error) {
      // console.error('Error deleting draft:', error);
      showSnackbar('Failed to delete draft. Please try again.', 'error');
    }
  }, [queryId, showSnackbar]);

  /**
   * Send a draft — converts the server-side draft to a final response.
   * No re-upload needed; files are already on the server.
   */
  const sendDraft = useCallback(async (draft, qId, canRespond, onQueryUpdate, onTimelineFetch) => {
    if (!canRespond) {
      showSnackbar('You do not have permission to send responses.', 'warning');
      return;
    }

    setIsSendingDraft(true);
    try {
      // The respond endpoint will find the draft by draftThreadId and convert it
      const payload = {
        message: draft.message,
        draftThreadId: draft._id,
        attachments: [],
      };

      const updatedQuery = await queryService.respondToQuery(qId, payload, {}, false);

      if (onQueryUpdate) {
        onQueryUpdate(updatedQuery);
      }
      if (onTimelineFetch) {
        onTimelineFetch({ silent: true });
      }

      showSnackbar('Response submitted successfully and visible to raiser', 'success');
    } catch (error) {
      const message = error.response?.data?.message || error.message || 'Failed to submit response. Please try again.';
      showSnackbar(message, 'error');
      throw error;
    } finally {
      setIsSendingDraft(false);
    }
  }, [showSnackbar]);

  /**
   * Reset draft state (e.g. when modal closes)
   */
  const resetDraftState = useCallback(() => {
    setIsSendingDraft(false);
  }, []);

  return {
    isSendingDraft,
    getDraftsFromThread,
    handleSaveDraft,
    deleteDraft,
    sendDraft,
    resetDraftState,
  };
};
